/**
 * One rule each for the contract pages (audit DUP-01 / CON-10 / DUP-04 / CON-24, owner 26 Sep 2026).
 * Pure: no React, no Firestore - the Contracts worker imports it too.
 *
 * The engine's twins: fines_pipeline/config.py CONTRACT_MONTHLY_MIN_DAYS and
 * fines_pipeline/normalization.py branch_code_of - change both sides together.
 */

/** A contract is MONTHLY from 30 days (pick-up -> drop-off); shorter is daily. It was 10 / 25 / 28 / 30 page by page. */
export const MONTHLY_MIN_DAYS = 30;

/** Shown for a contract whose status is blank: a blank status is never 'Open' (owner, 27 Sep 2026 - 4,184 empty
 *  records read as open contracts; Ejar always gives a status, so a blank one is unknown, not open). */
export const UNKNOWN_STATUS = 'Unknown';
/** A contract's status as stored (status, else Status), or UNKNOWN_STATUS when blank. */
export const contractStatusOf = (doc) => String(doc?.status || doc?.Status || '').trim() || UNKNOWN_STATUS;
/** Open only when the stored status is exactly Ejar's "Open". */
export const isOpenStatus = (doc) => contractStatusOf(doc).toLowerCase() === 'open';

/** The one name for a corporate / leasing contract (contract or booking number starting with 'C'). */
export const LEASING = 'Leasing';

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/** Days from pick-up to drop-off (partial days round up), or null when either date is missing. */
export const rentalDays = (start, end) => {
    if (!(start instanceof Date) || !(end instanceof Date) || isNaN(start) || isNaN(end)) return null;
    return Math.ceil(Math.abs(end.getTime() - start.getTime()) / MS_PER_DAY);
};

/** 'Monthly' | 'Daily' by the rental period, or null when it is unknown. */
export const periodKind = (start, end) => {
    const days = rentalDays(start, end);
    if (days === null) return null;
    return days >= MONTHLY_MIN_DAYS ? 'Monthly' : 'Daily';
};

// The Ejar branch code inside a contract / booking number: one letter, 4-8 digits, then D01 (Terminal 1),
// D02 (Terminal 2), D03 (Business Bay Branch) or D04 (Business Bay - Invygo) - B2609000D04477611,
// I2205D03285795, booking B26000D04441396. The old test /^[A-Z]\d{6}D04/ matched no real number.
const BRANCH_CODE_RE = /^[A-Z]\d{4,8}(D0[1-4])/;

/** 'D01' .. 'D04' from a contract or booking number; '' when it carries none (C2511ARE759740). */
export const branchCodeOf = (contractNo) => {
    const m = BRANCH_CODE_RE.exec(String(contractNo || '').trim().toUpperCase());
    return m ? m[1] : '';
};

/** The branch names of the codes; a number with no code (a corporate C... one) is 'Other'. */
export const BRANCH_NAMES = { D01: 'Terminal 1', D02: 'Terminal 2', D03: 'Business Bay Branch', D04: 'Business Bay - Invygo' };
export const branchNameOf = (contractNo) => BRANCH_NAMES[branchCodeOf(contractNo)] || 'Other';

/**
 * A Business Bay contract's Daily / Monthly as the engine decides it (contract_record.contract_kind - owner, 14 Oct
 * 2026: under 28 days daily, a part day counting as a day, unless its comments name a monthly rate), when the row
 * carries it (`contract_type` / 'Contract Type' / `contractType` from the store's `kind`); else null and the caller
 * keeps its own period rule. Business Bay only (code D03): every other branch keeps the 30-day period.
 */
export const businessBayKindOf = (row, contractNo) => {
    if (!row || branchCodeOf(contractNo ?? row.contractNo ?? row['Contract No.'] ?? row.contract_no ?? row.id) !== 'D03') return null;
    const kind = row.contract_type ?? row['Contract Type'] ?? row.contractType;
    return kind === 'Daily' || kind === 'Monthly' ? kind : null;
};

/** Branch code D04 = Business Bay - Invygo. */
export const isInvygoNumber = (contractNo) => branchCodeOf(contractNo) === 'D04';

const DUBAI_OFFSET_MS = 4 * 60 * 60 * 1000;   // UTC+4 all year (the UAE has no daylight saving)

/**
 * Today's Dubai calendar day as a Date at UTC midnight (the form parseSheetDate returns), whatever the
 * browser's clock zone. Between 00:00 and 04:00 Dubai the UTC date is still yesterday.
 */
export const dubaiToday = (now = new Date()) => {
    const d = new Date(now.getTime() + DUBAI_OFFSET_MS);
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
};
